/**
 * General utility functions
 */

// Single DOMParser instance for the application
const domParser = new DOMParser();

/**
 * Strips HTML tags from the given string.
 *
 * @param html the input HTML string
 * @returns the input stripped away from HTML tags.
 */
export const stripHTML = (html: string) => {
  const doc = domParser.parseFromString(html, 'text/html');
  return doc.body.textContent || '';
};

/**
 * A helper method to define which color is to be used for a text based on its background.
 *
 * @param color of a background
 * @returns color of a text that meets the contract criteria
 */
export const getTextColor = (color: string) => {
  if (!color) return '';
  const rgbColor = parseInt(color.substring(1), 16);
  const red = (rgbColor >> 16) & 0xff;
  const green = (rgbColor >> 8) & 0xff;
  const blue = (rgbColor >> 0) & 0xff;
  const luminance = 0.2126 * red + 0.7152 * green + 0.0722 * blue; // according to ITU-R BT.709-6

  return luminance < 186 ? '#fff' : ''; // 0...255
};

/**
 * Checks if a URL should be refreshed (either expired presigned URL or invalid presigned URL).
 *
 * For presigned URLs (with X-Amz-Date and X-Amz-Expires parameters):
 * - Returns true if expired or about to expire
 * - Returns true if malformed (only one parameter present)
 * - Returns false if still valid
 *
 * For non-presigned URLs (neither parameter present):
 * - Returns false (no refresh needed for permanent URLs)
 *
 * @param url the URL to check
 * @param bufferSeconds number of seconds before expiry to consider URL expired (default: 60)
 * @returns true if the URL should be refreshed (expired or invalid presigned URL)
 */
export const shouldRefreshUrl = (url: string, bufferSeconds = 60): boolean => {
  try {
    const urlObj = new URL(url);

    // Check for AWS Signature Version 4 parameters
    const amzDate = urlObj.searchParams.get('X-Amz-Date');
    const amzExpires = urlObj.searchParams.get('X-Amz-Expires');

    const hasDateParam = urlObj.searchParams.has('X-Amz-Date');
    const hasExpiresParam = urlObj.searchParams.has('X-Amz-Expires');

    // If neither parameter exists, it's not a presigned URL (no refresh needed)
    if (!hasDateParam && !hasExpiresParam) {
      return false;
    }

    // If only one parameter exists, it's a malformed presigned URL
    if (!hasDateParam || !hasExpiresParam) {
      return true;
    }

    // Empty or invalid values should trigger refresh (malformed presigned URL)
    if (!amzDate || !amzExpires || amzDate.trim() === '' || amzExpires.trim() === '') {
      return true;
    }

    // Parse and validate expiry duration
    const expiresIn = parseInt(amzExpires, 10);
    if (!Number.isFinite(expiresIn) || expiresIn < 0) {
      // Invalid or negative expiry duration, fail safe to refresh
      return true;
    }

    // Parse the date: format is YYYYMMDDTHHMMSSZ
    const year = parseInt(amzDate.substring(0, 4), 10);
    const month = parseInt(amzDate.substring(4, 6), 10) - 1; // months are 0-indexed
    const day = parseInt(amzDate.substring(6, 8), 10);
    const hour = parseInt(amzDate.substring(9, 11), 10);
    const minute = parseInt(amzDate.substring(11, 13), 10);
    const second = parseInt(amzDate.substring(13, 15), 10);

    // Validate parsed components are valid numbers
    if (
      !Number.isFinite(year) ||
      !Number.isFinite(month) ||
      !Number.isFinite(day) ||
      !Number.isFinite(hour) ||
      !Number.isFinite(minute) ||
      !Number.isFinite(second)
    ) {
      // Invalid date components, fail safe to refresh
      return true;
    }

    const signedDate = new Date(Date.UTC(year, month, day, hour, minute, second));

    // Validate signedDate is valid
    if (!Number.isFinite(signedDate.getTime())) {
      // Invalid date, fail safe to refresh
      return true;
    }

    // Validate that Date didn't roll over invalid values (e.g. Feb 32 -> Mar 4)
    if (
      signedDate.getUTCFullYear() !== year ||
      signedDate.getUTCMonth() !== month ||
      signedDate.getUTCDate() !== day ||
      signedDate.getUTCHours() !== hour ||
      signedDate.getUTCMinutes() !== minute ||
      signedDate.getUTCSeconds() !== second
    ) {
      // Date rolled over, fail safe to refresh
      return true;
    }

    const expiryDate = new Date(signedDate.getTime() + expiresIn * 1000);

    // Validate expiryDate is valid
    if (!Number.isFinite(expiryDate.getTime())) {
      // Invalid expiry date, fail safe to refresh
      return true;
    }

    const now = new Date();
    const expiryWithBuffer = new Date(expiryDate.getTime() - bufferSeconds * 1000);

    return now >= expiryWithBuffer;
  } catch (error) {
    // If we can't parse the URL, no refresh needed (not a valid URL)
    return false;
  }
};

/**
 * Validates if a URL is safe for download (blocks dangerous protocols).
 *
 * @param url the URL to validate
 * @returns true if the URL is safe, false otherwise
 */
const isSafeUrl = (url: string): boolean => {
  const normalizedUrl = url.toLowerCase().trim();

  const dangerousProtocols = ['javascript:', 'data:', 'vbscript:', 'file:', 'about:'];

  for (const protocol of dangerousProtocols) {
    if (normalizedUrl.startsWith(protocol)) {
      return false;
    }
  }

  try {
    const urlObj = new URL(normalizedUrl);
    const allowedProtocols = ['http:', 'https:', 'blob:'];
    return allowedProtocols.includes(urlObj.protocol);
  } catch {
    // Invalid URL
    return false;
  }
};

/**
 * Triggers a file download using a temporary anchor element.
 *
 * @param url the URL of the file to download
 * @param fileName the name to save the file as
 * @throws Error if the URL is not safe for download
 */
export const downloadFile = (url: string, fileName: string) => {
  if (!isSafeUrl(url)) {
    throw new Error('Unsafe URL: downloads are restricted to HTTP(S) and blob URLs');
  }

  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
